import React, { useContext, Suspense, lazy, Component } from "react" 
import { Grid, Container, useMediaQuery } from "@material-ui/core"
import { useRouteMatch, Switch, Route, useHistory, useLocation, Redirect } from "react-router-dom"
import { EventEmitterService, EventKey } from "../../services/EventEmitterService"
import UserProfileContext from "../../context/UserProfileContext"
import { FlowProtectedRoute } from "../Core/Routes/FlowProtectedRoute"
import { SessionContextStore } from "../../context/SessionContext"
import ViolationsService from "../../services/ViolationsService"
import { BrowserView, MobileView } from "react-device-detect"
import { InsurancePurchaseContextStore, setSelectionInStorage } from "../../context/InsuracePurchaseContext"
import { InsuranceBillingPeriod } from "../../models/insurance-models/InsuranceBillingPeriodModel"
import { SessionService } from "../../services/SessionService"
import FlexView from "react-flexview/lib"
import KeyboardBackspaceIcon from '@material-ui/icons/KeyboardBackspace';
import { lazyWithPreload } from "../../utilities/RLazyLoading"
import { BlueGrayBox } from "../Core/BlueGrayBox"
import { awaitWithLoading, billingPeriodPretty } from "../../services/utils"
import { FirebaseService, FirebaseConfigKey } from "../../services/FirebaseService"
import { COUNTRIES } from "../../Constants"
import { InsurerLogo } from "../Core/InsurerLogo"
import { ElevatedCircle } from "../Core/ElevatedCircle"
import Error from '@material-ui/icons/Error';
import { GuidelinesSummarySection } from "./SummarySection/GuidelinesSummarySection"
import { WhatsCoveredSummarySection } from "./SummarySection/WhatsCoveredSummarySection"
import DataLayerService from "../../services/DataLayerService"
import { PasswordlessEmailForm } from "../AbTestComponents/PasswordlessEmailForm"
import GoogleOptimizeService, { EXPERIMENTS, VARIANTS } from "../../services/GoogleOptimizeService"
import { BuyingInsuranceContextStore, FLOW_TYPES } from "../../context/BuyingInsuranceContext"
import { CustomizePolicyFleet } from "./CustomizePolicyFleet"

const CustomizePolicy = lazyWithPreload(() => import("./CustomizePolicy").then(module => ({ default: module.CustomizePolicy })))
CustomizePolicy.preload()
const InsuranceSetupSummary = lazyWithPreload(() => import("./InsuranceSetupSummary").then(module => ({ default: module.InsuranceSetupSummary })))
const UserProfile = lazyWithPreload(() => import('../UserProfile/UserProfile').then(module => ({ default: module.UserProfile })))
const Congratulations = lazyWithPreload(() => import("./Congratulations").then(module => ({ default: module.Congratulations })))
const UserConsentSuccess = lazyWithPreload(() => import("./UserConsentSuccess").then(module => ({ default: module.UserConsentSuccess })))
const CheckoutForm = lazyWithPreload(() => import("../Payment/CheckoutForm").then(module => ({ default: module.CheckoutForm })))
const SkywatchProgressBar = lazy(() => import( "../Core/SkywatchProgressBar").then(module => ({ default: module.SkywatchProgressBar })))
const PolicySummary = lazyWithPreload(() => import( "./PolicySummary").then(module => ({ default: module.PolicySummary })))
const FlowLogin = lazyWithPreload(() => import("../Login/FlowLogin").then(module => ({ default: module.FlowLogin })))

export const MonthlyInsurance = () => {
    const sessionContext = useContext(SessionContextStore)
    const insurancePurchaseContext = useContext(InsurancePurchaseContextStore)
    const offerContext = useContext(BuyingInsuranceContextStore)
    const { path, url } = useRouteMatch();
    const history = useHistory()
    const location = useLocation()
    const isMobile = !useMediaQuery(theme => theme.breakpoints.up('md'));

    React.useEffect(() => {
        const urlSearchParams = new URLSearchParams(window.location.search)
        if(urlSearchParams.size != 0){
            const params = Object.fromEntries(urlSearchParams.entries())
            if(params.utm_campaign !== undefined) {localStorage.setItem('utm_campaign', params.utm_campaign)}
            if(params.utm_medium !== undefined) {localStorage.setItem('utm_medium', params.utm_medium)}
            if(params.utm_source !== undefined) {localStorage.setItem('utm_source', params.utm_source)}
            if(params.utm_term !== undefined) {localStorage.setItem('utm_term', params.utm_term)}
            if(params.utm_content !== undefined) {localStorage.setItem('utm_content', params.utm_content)}
        }
    })

    React.useEffect(() => {
        setTimeout(() => {
            UserProfile.preload()
            InsuranceSetupSummary.preload()
            Congratulations.preload()
            UserConsentSuccess.preload()
            CheckoutForm.preload()
            PolicySummary.preload()
            FlowLogin.preload()
        }, 500)
    }, [])

    React.useEffect(() => {

        const fetchData = async () => {
            const urlParams = new URLSearchParams(window.location.search);
            if(urlParams.has('activationToken') && urlParams.get('userId')) 
            {
                let token = urlParams.get('activationToken')
                let userId = urlParams.get('userId')
                await awaitWithLoading(sessionContext.emailActivationLogin(userId, token))
                if (urlParams.get('selections')){
                    const selections = Buffer.from(urlParams.get('selections'), 'base64').toString('utf8');
                    const parsedSelections = JSON.parse(selections);
                    const { main, additional }= parsedSelections;
                    insurancePurchaseContext.setSelections(parsedSelections.main);
                    offerContext.setSelections(parsedSelections.additional);
                    setSelectionInStorage(parsedSelections.main);
                }
            }
        }

        fetchData()
        .catch(console.error);
    }, [])



    React.useEffect(() => {
        if (sessionContext.activeInsurance && sessionContext.activeInsurance.insuranceId && !SessionService.isBroker() && location.pathname !== '/insurance/congratulations') {
            EventEmitterService.dispatch(EventKey.ShowSuccess, {title: 'New Insurance?',
                body: "You already have an active policy",
                icon: <ElevatedCircle> <Error className="blue" /></ElevatedCircle>,
                actionText: "View my active policies",
                onClose: (val) => {if (val == 1) history.push('/policy-management')},
                closeBelowAction: () => {},
                closeBelowText: "Purchase new policy"});
        }
    }, [sessionContext.user.email])

    React.useEffect(() => {
        const handleFlow = (eventKey) => {
            switch(eventKey) {
                case EventKey.FLOW_CUSTOMIZE_FINISH:
                    if(!SessionService.isLoggedIn()) {
                        if(GoogleOptimizeService.getVariant(EXPERIMENTS.FLOW_POP_VS_PAGE) == VARIANTS.Variant1) {
                            DataLayerService.pushToDataLayer({'event': 'passwordless-page-shown'})
                            history.push(url + '/email')
                            break;
                        }
                    }
                    history.push(url + '/profile')
                    break
                case EventKey.FLOW_PROFILE_FINISH:
                    history.push(url + '/summary')
                    break
                case EventKey.FLOW_CC_FINISH:
                    history.push(url + '/summary')
                    break
                case EventKey.FLOW_SUMMARY_FINISH:
                    history.push(url + '/credit-card')
                    break
            }
        }

        let id2 = EventEmitterService.subscribe(EventKey.FLOW_GO_BACK, () => {
            switch(location.pathname) {
                case url + '/fleet':
                    history.replace(url + '/annual')
                    break;
                case url + '/profile':
                    if(offerContext.flowType === FLOW_TYPES.REGULAR) {
                        if(insurancePurchaseContext.selectedOption.billingPeriod == InsuranceBillingPeriod.MONTH){
                            history.replace(url + '/monthly')
                        }
                        else 
                            history.replace(url + '/annual')
                    } else {
                        history.replace(url + '/fleet')
                    }
                    break
                case url + '/credit-card':
                    history.replace(url + '/summary')
                    break
                case url + '/summary':
                    // if (SessionService.isBroker() && !FirebaseService.getBoolean(FirebaseConfigKey.PAYMENT_REQUIRED_FOR_BROKER)){
                    //     history.replace(url + '/profile')
                    // }
                    // else {
                    //     history.replace(url + '/credit-card')
                    // }
                    history.replace(url + '/profile')
                    break
                default: 
                    history.goBack()
                    break
            }
        })

        let id1 = EventEmitterService.subscribe(EventKey.FLOW_CUSTOMIZE_FINISH, () => {
            handleFlow(EventKey.FLOW_CUSTOMIZE_FINISH)
        })

        let id3 = EventEmitterService.subscribe(EventKey.FLOW_PROFILE_FINISH, () => {
            handleFlow(EventKey.FLOW_PROFILE_FINISH)
        })

        let id4 = EventEmitterService.subscribe(EventKey.FLOW_CC_FINISH, () => {
            handleFlow(EventKey.FLOW_CC_FINISH)
        })

        let id5 = EventEmitterService.subscribe(EventKey.FLOW_INSURANCE_PURCHASED_SUCCESS, (resp) => {
            history.push(url + '/congratulations', {resp: resp})
        })

        let id6 = EventEmitterService.subscribe(EventKey.FLOW_SUMMARY_FINISH, (resp) => {
            handleFlow(EventKey.FLOW_SUMMARY_FINISH)
        })

        return function cleanup () {
            EventEmitterService.unsubscribe(EventKey.FLOW_CUSTOMIZE_FINISH, id1)
            EventEmitterService.unsubscribe(EventKey.FLOW_GO_BACK, id2)
            EventEmitterService.unsubscribe(EventKey.FLOW_PROFILE_FINISH, id3)
            EventEmitterService.unsubscribe(EventKey.FLOW_CC_FINISH, id4)
            EventEmitterService.unsubscribe(EventKey.FLOW_INSURANCE_PURCHASED_SUCCESS, id5)
            EventEmitterService.unsubscribe(EventKey.FLOW_SUMMARY_FINISH, id6)
        }
    }, [sessionContext.user, location])

    
    const flowGoBack = () => EventEmitterService.dispatch(EventKey.FLOW_GO_BACK)
    return (
        <Switch>
            <Container>
                {location.pathname != '/insurance/congratulations' && location.pathname != '/declination' && location.pathname != '/uud' && !location.pathname.includes('consenttoglobal') && <>
                    <BrowserView>
                        <FlexView vAlignContent={'bottom'} className="space-between margin-top-basic">
                            <FlexView vAlignContent={'bottom'}>
                                <div className="text-promotion black bold">
                                    {insurancePurchaseContext.selectedOption?.offerId && <>
                                        {/* <span>
                                        {billingPeriodPretty(insurancePurchaseContext.selectedOption.billingPeriod)} 
                                    </span> */}
                                    <span>
                                        Drone {offerContext.flowType === FLOW_TYPES.FLEET || location.pathname === '/insurance/fleet' ? "Fleets " : ''}Insurance
                                    </span>
                                    </>}
                                </div>
                                {SessionService.isBroker() && <div className='margin-top-basic text-tiny margin-left-6'><BlueGrayBox>{sessionContext.user.email}</BlueGrayBox></div>}
                            </FlexView>
                            {location.pathname != '/insurance/summary' && <InsurerLogo />}    
                        </FlexView>
                    </BrowserView>
                    <MobileView>
                        <FlexView className="margin-top-small space-between" vAlignContent={'center'}>
                            <FlexView vAlignContent={'center'}>
                                {location.pathname != '/insurance/monthly' && location.pathname != '/insurance/annual' &&
                                    <KeyboardBackspaceIcon onClick={flowGoBack} className="black" /> }
                                <div className="text-title blue bold margin-left-basic">
                                    {/* <span>
                                        {billingPeriodPretty(insurancePurchaseContext.selectedOption.billingPeriod)} 
                                    </span> */}
                                    <span>
                                        Insurance Setup
                                    </span>
                                </div>
                                    {SessionService.isBroker() && <div className='text-tiny margin-left-6'><BlueGrayBox>{sessionContext.user.email}</BlueGrayBox></div>}
                            </FlexView>
                            {location.pathname != '/insurance/summary' && <InsurerLogo />}
                        </FlexView>
                    </MobileView>
                    <Suspense fallback={<div />}>
                    <Grid item sm={12} md={8}>
                        <SkywatchProgressBar />
                        </Grid>
                    </Suspense>
                
                    <Grid container>
                        <Grid item sm={12} md={8}>
                                {/* Incase path == /insurance , redirects to annual or monthly */}
                                <Route exact path={url}>
                                    <Redirect exact to={{pathname: `${url}/${FirebaseService.getValue(FirebaseConfigKey.COUNTRY) == COUNTRIES.UK ? 'annual': insurancePurchaseContext.selectedOption.billingPeriod == InsuranceBillingPeriod.MONTH ? 'monthly' : 'annual'}`, search: location.search}} /> 
                                </Route>

                                <UserProfileContext>
                                <Route path={`${path}/(monthly|annual)`}>
                                    <Suspense fallback={<div />}>
                                        <CustomizePolicy />
                                    </Suspense>
                                </Route>
                                <Route path={`${path}/fleet`}>
                                    <Suspense fallback={<div />}>
                                        <CustomizePolicyFleet />
                                    </Suspense>
                                </Route>
                                <Route path={`${path}/email`}>
                                    <Suspense fallback={<div />}>
                                        <PasswordlessEmailForm />
                                    </Suspense>
                                </Route>
                                <FlowProtectedRoute path={`${path}/profile`}>
                                    
                                        <Suspense fallback={<div />}>
                                            <UserProfile /> 
                                        </Suspense>
                                </FlowProtectedRoute>
                                 </UserProfileContext>
                                <FlowProtectedRoute path={`${path}/credit-card`}>
                                    <Suspense fallback={<div />}>
                                        <CheckoutForm /> 
                                    </Suspense>
                                </FlowProtectedRoute>
                                <FlowProtectedRoute path={`${path}/summary`}>
                                    <Suspense fallback={<div />}>
                                        <InsuranceSetupSummary /> 
                                    </Suspense>
                                </FlowProtectedRoute>
                                
                                {/* <Route path={`${path}/`}>
                                    <Redirect exact to={{pathname: `${url}/${FirebaseService.getValue(FirebaseConfigKey.COUNTRY) == COUNTRIES.UK ? 'annual': insurancePurchaseContext.selectedOption.billingPeriod == InsuranceBillingPeriod.MONTH ? 'monthly' : 'annual'}`, search: location.search}} /> 
                                </Route> */}

                                <Suspense fallback={<div />}>
                                    <FlowLogin finishedEvent={EventKey.FLOW_CUSTOMIZE_FINISH} />
                                </Suspense>
                        </Grid>
                        <Grid item sm={12} md={4}>
                            {location.pathname != '/insurance/congratulations' && location.pathname != '/insurance/summary' && !location.pathname.includes('consenttoglobal') && !isMobile && 
                                <Suspense fallback={<div />}>
                                    <PolicySummary />
                                </Suspense>}
                        </Grid>
                    </Grid>
                </>}
                <Route path={`${path}/congratulations`}>
                    <Suspense fallback={<div />}>
                        <Congratulations />
                    </Suspense>
                </Route>
                <Route path={`${path}/consenttoglobal/:token`}>
                    <Suspense fallback={<div />}>
                        <UserConsentSuccess />
                    </Suspense>
                </Route>
            </Container>
        </Switch>

    )
}